import CloseRoundedIcon from "@mui/icons-material/CloseRounded";
import DeleteOutlineRoundedIcon from "@mui/icons-material/DeleteOutlineRounded";
import SwapHorizRoundedIcon from "@mui/icons-material/SwapHorizRounded";
import { Button } from "@mui/material";

import { AppointmentExchangeResponse, AppointmentExchangeStatus } from "@/api";
import { ExchangeView } from "@/pages/exchange/Exchange.types";
import { useTranslation } from "@/translations";

interface Props {
  row: AppointmentExchangeResponse;
  view: ExchangeView;
  isBusy: boolean;
  onMakeOffer: (row: AppointmentExchangeResponse) => void;
  onWithdrawOffer: (row: AppointmentExchangeResponse) => void;
  onViewOffers: (row: AppointmentExchangeResponse) => void;
  onDeleteRequest: (row: AppointmentExchangeResponse) => void;
}

/**
 * The row action of the Tauschbörse. On colleagues' requests the user either makes an offer or takes
 * their pending one back; a rejected offer leaves no pending one, so "Angebot abgeben" returns by
 * itself. On the user's own requests an open one can be deleted, an erledigte one only reviewed —
 * both rows also open the offers on a plain row click, so the buttons stop the click from bubbling.
 */
export function ExchangeActionCell({
  row,
  view,
  isBusy,
  onMakeOffer,
  onWithdrawOffer,
  onViewOffers,
  onDeleteRequest,
}: Props) {
  const { t } = useTranslation();

  if (view === "mine") {
    if (row.status === AppointmentExchangeStatus.Open) {
      return (
        <Button
          variant="text"
          size="small"
          color="error"
          disabled={isBusy}
          startIcon={<DeleteOutlineRoundedIcon />}
          onClick={(event) => {
            event.stopPropagation();
            onDeleteRequest(row);
          }}
        >
          {t("exchange.actions.deleteRequest")}
        </Button>
      );
    }

    return (
      <Button
        variant="text"
        size="small"
        onClick={(event) => {
          event.stopPropagation();
          onViewOffers(row);
        }}
      >
        {t("exchange.actions.viewOffers")}
      </Button>
    );
  }

  if (row.ownPendingOfferId !== null) {
    return (
      <Button
        variant="text"
        size="small"
        color="error"
        disabled={isBusy}
        startIcon={<CloseRoundedIcon />}
        onClick={() => onWithdrawOffer(row)}
      >
        {t("exchange.actions.withdrawOffer")}
      </Button>
    );
  }

  return (
    <Button
      variant="text"
      size="small"
      disabled={isBusy}
      startIcon={<SwapHorizRoundedIcon />}
      onClick={() => onMakeOffer(row)}
    >
      {t("exchange.actions.makeOffer")}
    </Button>
  );
}
